import { memo, useCallback, useState } from 'react';
import { theme } from './constants';

const Sidebar = ({
    config,
    selectedSlot,
    setSelectedSlot,
    setIsOptionsOpen,
    setHoveredSlot,
    componentOptions,
    isAuthenticated,
    extraCost,
    handleExtraCostInput,
    isPriceLoading,
    prices,
    extraCostValue,
    configCode,
    handleCodeInput,
    copyToClipboard,
    isCopied,
    handleReset,
    setIsContactModalOpen,
    cameraTargetPos,
    controlsTargetLookAt,
    isAutoMoving,
    onOpenWarning,
    buttonColors,
    setButtonColors
}) => {
    const [manualDiscount, setManualDiscount] = useState(0);
    const listPrice = (prices?.total ?? 0) + extraCostValue;
    const netPrice = listPrice * (1 - (manualDiscount / 100));
    const handleColorChange = (id, color) => {
        setButtonColors(prev => ({ ...prev, [id]: color }));
    };

    const handleSlotClick = useCallback((id) => {
        if (config.Q5.variant === 'none' && ['Q6', 'Q7', 'Q8'].includes(id)) {
            onOpenWarning();
            return;
        }

        setSelectedSlot(id);
        setIsOptionsOpen(true);
        if (isAutoMoving) isAutoMoving.current = true;

        if (id === 'Q1' || id === 'Q3') {
            cameraTargetPos.current.set(3, -5.5, 1);
            controlsTargetLookAt.current.set(0, -1.5, 0);
        } else if (id === 'Q2' || id === 'Q4') {
            cameraTargetPos.current.set(3, 5.5, 1);
            controlsTargetLookAt.current.set(0, 1.5, 0);
        } else if (id === 'inner_components') {
            cameraTargetPos.current.set(5, 1, -2);
            controlsTargetLookAt.current.set(0, 0, 0);
        } else {
            cameraTargetPos.current.set(6, 0.5, 1.9);
            controlsTargetLookAt.current.set(0, 0, 0);
        }
    }, [setSelectedSlot, setIsOptionsOpen, isAutoMoving, cameraTargetPos, controlsTargetLookAt, config, onOpenWarning]);

    const onMouseEnter = useCallback((id) => setHoveredSlot(id), [setHoveredSlot]);
    const onMouseLeave = useCallback(() => setHoveredSlot(null), [setHoveredSlot]);

    return (
        <div style={{ width: '415px', background: 'white', borderLeft: `1px solid ${theme.border}`, padding: '90px 20px 20px 20px', pointerEvents: 'auto', zIndex: 2, display: 'flex', flexDirection: 'column' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 700, margin: '0 0 15px 0', letterSpacing: '0.5px' }}>CONFIGURATION</h2>
            <div className="custom-scrollbar" style={{ flex: 1, overflowY: 'auto', paddingRight: '10px' }}>
                {/* Slot List */}
                {['Q1', 'Q2', 'Q3', 'Q4', 'Q5', 'Q6', 'Q7', 'Q8', 'Q9'].map((id) => (
                    <div
                        key={id}
                        onMouseEnter={() => onMouseEnter(id)}
                        onMouseLeave={onMouseLeave}
                        onClick={() => handleSlotClick(id)}
                        style={{
                            padding: '14px',
                            borderRadius: '12px',
                            marginBottom: '8px',
                            border: `1px solid ${selectedSlot === id ? theme.primary : theme.border}`,
                            background: 'rgb(255, 215, 0)',
                            cursor: 'pointer'
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ fontSize: '16px', fontWeight: 700 }}>
                                <span style={{ color: theme.primary, marginRight: '8px' }}>{id}:</span>
                                {config[id]?.name || 'Unknown'}
                            </div>
                            <span style={{ fontSize: '10px', color: theme.textMuted, fontWeight: 500 }}>
                                {componentOptions[id]?.label.toUpperCase() || ''}
                            </span>
                        </div>
                    </div>
                ))}

                {/* Internal Hardware */}
                <div
                    onMouseEnter={() => onMouseEnter('inner_components')}
                    onMouseLeave={onMouseLeave}
                    onClick={() => handleSlotClick('inner_components')}
                    style={{
                        padding: '20px',
                        borderRadius: '12px',
                        border: `2px dashed ${selectedSlot === 'inner_components' ? 'rgb(255, 215, 0)' : 'rgb(0, 40, 70)'}`,
                        background: selectedSlot === 'inner_components' ? '#eff6ff' : '#f8fafc',
                        cursor: 'pointer',
                        textAlign: 'center',
                        height: 'auto',
                        marginBottom: '20px',
                        marginTop: '20px'
                    }}
                >
                    <div style={{ fontSize: '12px', fontWeight: 900, color: theme.primary, marginBottom: '4px', letterSpacing: '1px' }}>INTERNAL HARDWARE</div>
                    <div style={{ fontSize: '12px', color: theme.textMuted, marginTop: '8px', lineHeight: '1.5' }}>
                        {config.inner_components.length > 0 ? (
                            config.inner_components.map((item, idx) => (
                                <div key={idx} style={{ fontWeight: 500, color: theme.textMain }}>{item.name}</div>
                            ))
                        ) : ('NO OPTIONS SELECTED')}
                    </div>
                </div>

                {/* Pricing Section */}
                <div style={{ marginTop: '10px' }}>
                    {isAuthenticated ? (
                        <>
                            {/* Extra Charges Input */}
                            <div style={{ background: theme.bg, padding: '15px', borderRadius: '12px', border: `1px solid ${theme.border}`, marginBottom: '15px' }}>
                                <div style={{ fontSize: '12px', fontWeight: 900, color: theme.textMuted, marginBottom: '8px', letterSpacing: '1px' }}>ADDITIONAL CHARGES</div>
                                <input type="text" value={extraCost} placeholder="€0" onChange={(e) => handleExtraCostInput(e.target.value)} style={{ width: '100%', background: 'white', border: `1px solid ${theme.border}`, padding: '12px', borderRadius: '12px', fontSize: '14px', fontWeight: 700, color: theme.primary, outline: 'none' }} />
                            </div>

                            {/* --- UPDATED PRICE CARD (Dark Blue) --- */}
                            <div style={{
                                background: 'rgb(0, 40, 70)', // RESTORED OLD COLOR
                                borderRadius: '12px',
                                padding: '20px',
                                marginBottom: '15px',
                                color: 'white', // White text for contrast
                                textAlign: 'center'
                            }}>
                                {/* TOP: Discount Input */}
                                <div style={{ marginBottom: '15px', textAlign: 'left' }}>
                                    <label style={{
                                        display: 'block',
                                        fontSize: '11px',
                                        fontWeight: '900',
                                        color: 'rgba(255,255,255,0.7)', // Light gray label
                                        marginBottom: '6px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Manual Discount (%)
                                    </label>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            value={manualDiscount}
                                            onFocus={(e) => e.target.select()}
                                            onChange={(e) => {
                                                const val = parseFloat(e.target.value);
                                                if (isNaN(val)) setManualDiscount(0);
                                                else setManualDiscount(Math.min(100, Math.max(0, val)));
                                            }}
                                            style={{
                                                width: '100%',
                                                padding: '8px 12px',
                                                paddingRight: '30px',
                                                borderRadius: '6px',
                                                border: 'none',
                                                fontSize: '14px',
                                                fontWeight: '700',
                                                color: 'rgb(0, 40, 70)',
                                                outline: 'none',
                                                boxSizing: 'border-box',
                                                background: 'white'
                                            }}
                                        />
                                        <span style={{
                                            position: 'absolute',
                                            right: '12px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            color: 'rgb(0, 40, 70)',
                                            fontWeight: '700',
                                            fontSize: '14px'
                                        }}>%</span>
                                    </div>
                                </div>

                                {/* MIDDLE: Divider */}
                                <hr style={{ border: '0', borderTop: '1px solid rgba(255,255,255,0.2)', margin: '15px 0' }} />

                                {/* BOTTOM: Price Display */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>

                                    {/* List Price Row */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', fontWeight: '600' }}>List Price:</span>
                                        <span style={{
                                            fontSize: '13px',
                                            color: 'rgba(255,255,255,0.7)',
                                            fontWeight: '600'
                                        }}>
                                            {isPriceLoading ? '...' : `€${listPrice.toLocaleString('en-DE', { minimumFractionDigits: 2 })}`}
                                        </span>
                                    </div>

                                    {/* Net Price Row */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ fontSize: '15px', color: 'white', fontWeight: '900' }}>Net Price:</span>
                                        <span style={{ fontSize: '24px', color: 'white', fontWeight: '900' }}>
                                            {isPriceLoading ? '...' : `€${netPrice.toLocaleString('en-DE', { minimumFractionDigits: 2 })}`}
                                        </span>
                                    </div>

                                    {/* Savings Text */}
                                    {manualDiscount > 0 && !isPriceLoading && (
                                        <div style={{ textAlign: 'right', fontSize: '11px', color: '#4ade80', fontWeight: '700', marginTop: '-4px' }}>
                                            - €{(listPrice - netPrice).toLocaleString('en-DE', { minimumFractionDigits: 2 })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div onClick={() => setIsContactModalOpen(true)} style={{ padding: '35px 20px', borderRadius: '12px', background: 'rgb(0, 40, 70)', color: 'white', marginBottom: '15px', textAlign: 'center', cursor: 'pointer' }}>
                            <div style={{ fontSize: '24px', fontWeight: 900, letterSpacing: '1px' }}>CONTACT SALES</div>
                        </div>
                    )}
                </div>

                {/* Config Code */}
                <div style={{ background: theme.bg, padding: '15px', borderRadius: '12px', border: `1px solid ${theme.border}`, marginBottom: '15px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 900, color: theme.textMuted, marginBottom: '8px' }}>CONFIG CODE</div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <input type="text" value={configCode} onChange={(e) => handleCodeInput(e.target.value)} onFocus={(e) => e.target.select()} style={{ flex: 1, background: 'white', border: `1px solid ${theme.border}`, padding: '10px', borderRadius: '8px', fontSize: '12px', fontFamily: 'monospace', fontWeight: 700, color: theme.primary }} />
                        <button onClick={copyToClipboard} style={{ padding: '0 12px', background: isCopied ? '#22c55e' : theme.primary, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '10px', fontWeight: 900 }}>{isCopied ? 'COPIED!' : 'COPY'}</button>
                    </div>
                </div>
                <button onClick={handleReset} style={{ width: '100%', padding: '15px', borderRadius: '12px', border: 'none', background: '#fee2e2', color: theme.danger, fontWeight: 900, cursor: 'pointer', marginBottom: '20px' }}>RESET FACTORY SETTINGS</button>
            </div>
        </div>
    );
};

export default memo(Sidebar);